import { MessageDomains } from "./message.domain";
import { MessageActions } from "./message.actions";
import { Feature } from '../constants/features';

/**
 * Base message interface that all widget messages must implement.
 * Every message MUST have a type (domain) and action field for proper routing and handling.
 */
export interface BaseMessage {
    type: string;
    action: string;
}
/**
 * Modal domain messages
 */
export interface ModalCloseMessage extends BaseMessage {
    type: typeof MessageDomains.MODAL;
    action: typeof MessageActions.CLOSE;
}

export interface ModalOpenMessage extends BaseMessage {
    type: typeof MessageDomains.MODAL;
    action: typeof MessageActions.OPEN;
    modalId?: string;
}

/**
 * Feature domain messages
 */
export interface FeatureSelectMessage extends BaseMessage {
    type: typeof MessageDomains.FEATURE;
    action: typeof MessageActions.SELECT;
    feature: Feature;
    data?: Record<string, unknown>;
}

export interface FeatureUnselectMessage extends BaseMessage {
    type: typeof MessageDomains.FEATURE;
    action: typeof MessageActions.UNSELECT;
    feature: Feature;
}

/**
 * Language domain messages
 */
export interface LanguageChangeMessage extends BaseMessage {
    type: typeof MessageDomains.LANGUAGE;
    action: typeof MessageActions.CHANGE;
    language: string;
}

/**
 * Flyout domain messages
 */
export interface FlyoutToggleMessage extends BaseMessage {
    type: typeof MessageDomains.FLYOUT;
    action: typeof MessageActions.TOGGLE;
    isOpen: boolean;
}

/**
 * Interface domain messages
 */
export interface InterfaceHideMessage extends BaseMessage {
    type: typeof MessageDomains.INTERFACE;
    action: typeof MessageActions.HIDE;
}

export interface InterfaceShowMessage extends BaseMessage {
    type: typeof MessageDomains.INTERFACE;
    action: typeof MessageActions.SHOW;
}

export interface InterfaceResetMessage extends BaseMessage {
    type: typeof MessageDomains.INTERFACE;
    action: typeof MessageActions.RESET;
}

/**
 * Navigation domain messages
 */
export interface NavigationVoiceStartMessage extends BaseMessage {
    type: typeof MessageDomains.NAVIGATION;
    action: typeof MessageActions.VOICE_START;
    languageCode?: string;
}

export interface NavigationPageStructureMessage extends BaseMessage {
    type: typeof MessageDomains.NAVIGATION;
    action: typeof MessageActions.PAGE_STRUCTURE;
}

/**
 * Color domain messages
 */
export interface ColorChangeBackgroundMessage extends BaseMessage {
    type: typeof MessageDomains.COLOR;
    action: typeof MessageActions.CHANGE_BACKGROUND;
    color: string;
}

export interface ColorChangeTextMessage extends BaseMessage {
    type: typeof MessageDomains.COLOR;
    action: typeof MessageActions.CHANGE_TEXT;
    color: string;
}

/**
 * Mouse domain messages
 */
export interface MouseMoveMessage extends BaseMessage {
    type: typeof MessageDomains.MOUSE;
    action: typeof MessageActions.MOVE;
    coordinates: {
        x: number;
        y: number;
    };
}

/**
 * Union of all possible widget messages
 */
export type WidgetMessage =
    | ModalCloseMessage
    | ModalOpenMessage
    | FeatureSelectMessage
    | FeatureUnselectMessage
    | LanguageChangeMessage
    | FlyoutToggleMessage
    | InterfaceHideMessage
    | InterfaceShowMessage
    | InterfaceResetMessage
    | NavigationVoiceStartMessage
    | NavigationPageStructureMessage
    | ColorChangeBackgroundMessage
    | ColorChangeTextMessage
    | MouseMoveMessage

/**
 * Type guard to check if a message is a valid widget message
 */
export const isValidWidgetMessage = (message: any): message is WidgetMessage => {
    return (
        message && 
        typeof message === 'object' && 
        typeof message.type === 'string' && 
        typeof message.action === 'string'
    );
};